import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  Firestore,
  arrayUnion,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  serverTimestamp,
  deleteField,
  Timestamp,
  writeBatch,
} from '@angular/fire/firestore';
import { type RolCooperativaPlantilla } from './cooperativa-usuarios.model';
import {
  PERMISOS_VERSION,
  normalizarPermisosModulosMap,
  permisosBasicosDesdeModulos,
  permisosModulosInicialesSegunRol,
  type PermisosModulosMap,
} from './permisos-catalogo';

const COLL = 'cooperativa';
const SUB_USUARIOS = 'usuarios';
const SUB_INVITACIONES = 'invitacionesUsuario';

/** Permisos básicos (ampliar cuando definan eventos / módulos). */
export const PERMISOS_BASICOS_KEYS = [
  'workspace',
  'socios',
  'administracionUsuarios',
  'datosCooperativa',
  'integracionAlmacenamiento',
  'catalogoMonotributo',
  'clientesLugaresCatalogo',
  'regionesCatalogo',
  'serviciosCatalogo',
  'servicioSaludCatalogo',
  'liquidacionConceptosCatalogo',
  'periodosGestion',
] as const;

export type PermisoBasicoKey = (typeof PERMISOS_BASICOS_KEYS)[number];

export function permisosBasicosPorDefecto(): Record<PermisoBasicoKey, boolean> {
  return {
    workspace: true,
    socios: true,
    administracionUsuarios: false,
    datosCooperativa: false,
    integracionAlmacenamiento: false,
    catalogoMonotributo: false,
    clientesLugaresCatalogo: false,
    regionesCatalogo: false,
    serviciosCatalogo: false,
    servicioSaludCatalogo: false,
    liquidacionConceptosCatalogo: false,
    periodosGestion: false,
  };
}

/** Al invitar: roles de gestión arrancan con permiso de administrar usuarios (ajustable después en el panel). */
export function permisosInicialesSegunRolPlantilla(role: string): Record<PermisoBasicoKey, boolean> {
  const modulos = permisosModulosInicialesSegunRol(role);
  return permisosBasicosDesdeModulos(modulos) as Record<PermisoBasicoKey, boolean>;
}

export interface PermisosUsuarioPayload {
  permisos: Record<string, boolean>;
  permisosModulos: PermisosModulosMap;
  permisosVersion: number;
}

export function construirPayloadPermisos(modulos: PermisosModulosMap): PermisosUsuarioPayload {
  const permisosModulos = normalizarPermisosModulosMap(modulos);
  const permisos = permisosBasicosDesdeModulos(permisosModulos) as Record<PermisoBasicoKey, boolean>;
  return {
    permisos,
    permisosModulos,
    permisosVersion: PERMISOS_VERSION,
  };
}

export function normalizarEmailDocId(email: string): string {
  return email.trim().toLowerCase();
}

export type EstadoInvitacionCoop = 'pendiente' | 'vinculado';

export interface FilaUsuarioCooperativa {
  /** uid de Auth si está vinculado, o null si solo invitación */
  uid: string | null;
  /** id del doc de invitación (= email normalizado) si aplica */
  invitacionDocId: string | null;
  email: string;
  role: string;
  permisos: Record<string, boolean>;
  permisosModulos: PermisosModulosMap;
  estado: 'pendiente' | 'vinculado';
  /** Soft delete: oculto en listados operativos */
  eliminadoEn: Date | null;
  invitedAt: Date | null;
  vinculadoAt: Date | null;
  assignedAt: Date | null;
  emailEnviadoEn: Date | null;
  /** Mensaje si falló el envío SMTP (Cloud Function). */
  emailEnvioError: string | null;
}

function tsToDate(t: unknown): Date | null {
  if (t instanceof Timestamp) return t.toDate();
  return null;
}

@Injectable({ providedIn: 'root' })
export class CooperativaUsuariosTenantService {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);

  private refInvitacion(cooperativaId: string, emailDocId: string) {
    return doc(this.firestore, COLL, cooperativaId, SUB_INVITACIONES, emailDocId);
  }

  private refUsuario(cooperativaId: string, uid: string) {
    return doc(this.firestore, COLL, cooperativaId, SUB_USUARIOS, uid);
  }

  /**
   * Tras el login: crea `usuarios/{uid}` y marca invitación como vinculada
   * si hay invitación pendiente con el mismo correo que el token.
   * Prioriza la callable (Admin SDK, email normalizado); si falla, intenta batch en cliente.
   * @returns IDs de cooperativas vinculadas en esta ejecución (para fijar sesión activa).
   */
  async vincularInvitacionesPendientes(): Promise<string[]> {
    const user = this.auth.currentUser;
    if (!user?.email?.trim()) return [];

    try {
      const fn = httpsCallable<object, { linkedCoopIds?: string[] }>(
        this.functions,
        'vincularInvitacionesAlUsuario'
      );
      const result = await fn({});
      const ids = result.data?.linkedCoopIds;
      if (Array.isArray(ids)) {
        return ids;
      }
    } catch (e) {
      console.warn(
        '[CooperativaUsuarios] Callable vincularInvitacionesAlUsuario no disponible o falló; reintento vía Firestore:',
        e
      );
    }

    return this.vincularInvitacionesPendientesDesdeCliente();
  }

  /** Respaldo si la callable no está desplegada; puede fallar por reglas si el email del token no coincide en mayúsculas con el id del doc. */
  private async vincularInvitacionesPendientesDesdeCliente(): Promise<string[]> {
    const user = this.auth.currentUser;
    const email = user?.email?.trim();
    if (!user || !email) return [];

    const emailId = normalizarEmailDocId(email);
    const q = query(
      collectionGroup(this.firestore, SUB_INVITACIONES),
      where('email', '==', emailId)
    );
    const snap = await getDocs(q);
    const batch = writeBatch(this.firestore);
    let n = 0;
    const linkedCoopIds: string[] = [];

    for (const d of snap.docs) {
      const data = d.data() as Record<string, unknown>;
      if (String(data['estado'] ?? '') !== 'pendiente') continue;
      if (data['eliminadoEn']) continue;

      const coopRef = d.ref.parent.parent;
      if (!coopRef) continue;
      const coopId = coopRef.id;

      const role = String(data['role'] ?? 'socio');
      const permisos = (data['permisos'] as Record<string, boolean> | undefined) ?? permisosBasicosPorDefecto();
      const permisosModulos = normalizarPermisosModulosMap(
        data['permisosModulos'] as PermisosModulosMap | undefined
      );
      const modulosEfectivos =
        Object.keys(permisosModulos).length > 0 ? permisosModulos : permisosModulosInicialesSegunRol(role);

      const uRef = this.refUsuario(coopId, user.uid);
      const uSnap = await getDoc(uRef);
      if (uSnap.exists()) {
        continue;
      }

      batch.set(uRef, {
        role,
        permisos,
        permisosModulos: modulosEfectivos,
        permisosVersion: PERMISOS_VERSION,
        email: emailId,
        assignedAt: serverTimestamp(),
        linkedFromInvitation: true,
      });

      batch.update(d.ref, {
        estado: 'vinculado' satisfies EstadoInvitacionCoop,
        uidVinculado: user.uid,
        vinculadoAt: serverTimestamp(),
      });
      linkedCoopIds.push(coopId);
      n++;
    }

    if (n > 0) {
      await batch.commit();
      try {
        await setDoc(
          doc(this.firestore, 'users', user.uid),
          {
            cooperativaAccesoIds: arrayUnion(...linkedCoopIds),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.warn('[CooperativaUsuarios] No se pudo actualizar users.cooperativaAccesoIds:', e);
      }
    }
    return linkedCoopIds;
  }

  /**
   * IDs de cooperativas donde el usuario tiene `usuarios/{uid}`.
   * Prioriza la callable (Admin SDK, sin límites de consulta del cliente); si falla, usa `users/{uid}`.
   */
  async cooperativasDondeUsuarioTieneAcceso(): Promise<string[]> {
    const user = this.auth.currentUser;
    if (!user?.email?.trim()) return [];

    const desdeUsersP = this.cooperativasAccesoIdsDesdeUsersDoc(user.uid);
    const desdeCallableP = (async (): Promise<string[]> => {
      try {
        const fn = httpsCallable<object, { cooperativaIds?: string[] }>(
          this.functions,
          'listarCooperativasAccesoUsuario'
        );
        const result = await fn({});
        const ids = result.data?.cooperativaIds;
        return Array.isArray(ids) ? ids.filter((x) => typeof x === 'string' && x.trim()) : [];
      } catch (e) {
        console.warn(
          '[CooperativaUsuarios] listarCooperativasAccesoUsuario no disponible o falló; usando users/:',
          e
        );
        return [];
      }
    })();

    const [desdeUsers, desdeCallable] = await Promise.all([desdeUsersP, desdeCallableP]);
    const merged = new Set<string>();
    for (const id of [...desdeUsers, ...desdeCallable]) {
      merged.add(id.trim());
    }
    return [...merged].sort();
  }

  private async cooperativasAccesoIdsDesdeUsersDoc(uid: string): Promise<string[]> {
    const snap = await getDoc(doc(this.firestore, 'users', uid));
    if (!snap.exists()) return [];
    const ids = new Set<string>();
    const arr = snap.data()?.['cooperativaAccesoIds'];
    if (Array.isArray(arr)) {
      for (const x of arr) {
        if (typeof x === 'string' && x.trim()) ids.add(x.trim());
      }
    }
    const pref = snap.data()?.['cooperativaPreferidaId'];
    if (typeof pref === 'string' && pref.trim()) ids.add(pref.trim());
    return [...ids].sort();
  }

  async listarFilas(cooperativaId: string): Promise<FilaUsuarioCooperativa[]> {
    const [usuSnap, invSnap] = await Promise.all([
      getDocs(collection(this.firestore, COLL, cooperativaId, SUB_USUARIOS)),
      getDocs(collection(this.firestore, COLL, cooperativaId, SUB_INVITACIONES)),
    ]);

    const filas: FilaUsuarioCooperativa[] = [];
    const emailsVinculados = new Set<string>();

    for (const d of usuSnap.docs) {
      const data = d.data() as Record<string, unknown>;
      if (data['eliminadoEn']) {
        continue;
      }

      const em = String(data['email'] ?? '').trim().toLowerCase();
      if (em) emailsVinculados.add(em);

      const role = String(data['role'] ?? '—');
      const rawMod = data['permisosModulos'] as PermisosModulosMap | undefined;
      const permisosModulos =
        rawMod && Object.keys(rawMod).length > 0
          ? normalizarPermisosModulosMap(rawMod)
          : permisosModulosInicialesSegunRol(role);

      filas.push({
        uid: d.id,
        invitacionDocId: null,
        email: em || '—',
        role,
        permisos: (data['permisos'] as Record<string, boolean>) ?? permisosBasicosPorDefecto(),
        permisosModulos,
        estado: 'vinculado',
        eliminadoEn: null,
        invitedAt: null,
        vinculadoAt: null,
        assignedAt: tsToDate(data['assignedAt']),
        emailEnviadoEn: null,
        emailEnvioError: null,
      });
    }

    for (const d of invSnap.docs) {
      const data = d.data() as Record<string, unknown>;
      if (data['eliminadoEn']) continue;
      const email = String(data['email'] ?? d.id).trim().toLowerCase();
      const estado = String(data['estado'] ?? 'pendiente') as EstadoInvitacionCoop;

      if (estado === 'vinculado') {
        continue;
      }

      if (emailsVinculados.has(email)) {
        continue;
      }

      const roleInv = String(data['role'] ?? 'socio');
      const rawModInv = data['permisosModulos'] as PermisosModulosMap | undefined;
      const permisosModulosInv =
        rawModInv && Object.keys(rawModInv).length > 0
          ? normalizarPermisosModulosMap(rawModInv)
          : permisosModulosInicialesSegunRol(roleInv);

      filas.push({
        uid: null,
        invitacionDocId: d.id,
        email,
        role: roleInv,
        permisos: (data['permisos'] as Record<string, boolean>) ?? permisosBasicosPorDefecto(),
        permisosModulos: permisosModulosInv,
        estado: 'pendiente',
        eliminadoEn: null,
        invitedAt: tsToDate(data['invitedAt']),
        vinculadoAt: tsToDate(data['vinculadoAt']),
        assignedAt: null,
        emailEnviadoEn: tsToDate(data['emailEnviadoEn']),
        emailEnvioError:
          typeof data['emailEnvioError'] === 'string' && String(data['emailEnvioError']).trim()
            ? String(data['emailEnvioError']).trim()
            : null,
      });
    }

    filas.sort((a, b) => a.email.localeCompare(b.email, 'es', { sensitivity: 'base' }));
    return filas;
  }

  async crearInvitacion(
    cooperativaId: string,
    emailRaw: string,
    role: RolCooperativaPlantilla,
    invitedByUid: string | null
  ): Promise<{ emailDocId: string }> {
    const emailDocId = normalizarEmailDocId(emailRaw);
    if (!emailDocId || !emailRaw.includes('@')) {
      throw new Error('Ingresá un correo electrónico válido.');
    }

    const invRef = this.refInvitacion(cooperativaId, emailDocId);
    const existing = await getDoc(invRef);
    if (existing.exists()) {
      const d = existing.data() as { eliminadoEn?: unknown; estado?: string };
      if (!d.eliminadoEn && String(d.estado ?? 'pendiente') === 'pendiente') {
        throw new Error('Ya existe una invitación pendiente para ese correo.');
      }
      if (!d.eliminadoEn && String(d.estado) === 'vinculado') {
        throw new Error('Ese correo ya fue vinculado. Quitá el acceso del usuario si necesitás reinvitar.');
      }
    }

    const usuQ = await getDocs(collection(this.firestore, COLL, cooperativaId, SUB_USUARIOS));
    for (const u of usuQ.docs) {
      const ud = u.data() as { email?: string; eliminadoEn?: unknown };
      if (ud['eliminadoEn']) continue;
      const ue = String(ud['email'] ?? '').trim().toLowerCase();
      if (ue === emailDocId) {
        throw new Error('Ese correo ya está vinculado a un usuario en esta cooperativa.');
      }
    }

    const payloadPerm = construirPayloadPermisos(permisosModulosInicialesSegunRol(role));

    await setDoc(
      invRef,
      {
        email: emailDocId,
        role,
        permisos: payloadPerm.permisos,
        permisosModulos: payloadPerm.permisosModulos,
        permisosVersion: payloadPerm.permisosVersion,
        estado: 'pendiente' satisfies EstadoInvitacionCoop,
        invitedAt: serverTimestamp(),
        invitedByUid: invitedByUid ?? null,
        emailEnviadoEn: null,
        emailPendienteEnvio: true,
        eliminadoEn: null,
      },
      { merge: true }
    );

    return { emailDocId };
  }

  async actualizarRolInvitacion(
    cooperativaId: string,
    emailDocId: string,
    role: string
  ): Promise<void> {
    await updateDoc(this.refInvitacion(cooperativaId, emailDocId), {
      role: role.trim(),
    });
  }

  async actualizarRolUsuario(cooperativaId: string, uid: string, role: string): Promise<void> {
    await updateDoc(this.refUsuario(cooperativaId, uid), {
      role: role.trim(),
    });
  }

  async actualizarPermisosInvitacion(
    cooperativaId: string,
    emailDocId: string,
    payload: PermisosUsuarioPayload
  ): Promise<void> {
    await updateDoc(this.refInvitacion(cooperativaId, emailDocId), {
      permisos: payload.permisos,
      permisosModulos: payload.permisosModulos,
      permisosVersion: payload.permisosVersion,
      permisosActualizadosEn: serverTimestamp(),
    });
  }

  async actualizarPermisosUsuario(
    cooperativaId: string,
    uid: string,
    payload: PermisosUsuarioPayload
  ): Promise<void> {
    await updateDoc(this.refUsuario(cooperativaId, uid), {
      permisos: payload.permisos,
      permisosModulos: payload.permisosModulos,
      permisosVersion: payload.permisosVersion,
      permisosActualizadosEn: serverTimestamp(),
    });
  }

  async softDeleteInvitacion(cooperativaId: string, emailDocId: string): Promise<void> {
    await updateDoc(this.refInvitacion(cooperativaId, emailDocId), {
      eliminadoEn: serverTimestamp(),
    });
  }

  async softDeleteUsuario(cooperativaId: string, uid: string): Promise<void> {
    await updateDoc(this.refUsuario(cooperativaId, uid), {
      eliminadoEn: serverTimestamp(),
    });
  }

  /**
   * Hook para Firebase Functions: marcar que se encoló el envío.
   * Hasta entonces la UI puede mostrar "correo pendiente de envío".
   */
  async marcarInvitacionNotificada(cooperativaId: string, emailDocId: string): Promise<void> {
    await updateDoc(this.refInvitacion(cooperativaId, emailDocId), {
      emailEnviadoEn: serverTimestamp(),
      emailPendienteEnvio: false,
    });
  }

  /**
   * Dispara de nuevo la Cloud Function de correo (onDocumentWritten).
   * Útil tras corregir SMTP o si el doc quedó colgado sin procesar.
   */
  async solicitarEnvioCorreoInvitacion(cooperativaId: string, emailDocId: string): Promise<void> {
    await updateDoc(this.refInvitacion(cooperativaId, emailDocId), {
      emailEnvioError: deleteField(),
      emailPendienteEnvio: true,
      correoInvitacionSolicitudEn: serverTimestamp(),
    });
  }
}
